import React, { useState, useEffect } from 'react';
import { Table, Button, Card, Alert, Row, Col, Form, Badge, Pagination } from 'react-bootstrap';
import { FaEdit, FaTrash, FaPlus, FaSearch, FaUser, FaUserCheck, FaUserTimes } from 'react-icons/fa';
import { usersAPI } from '../../services/api';
import FormModal from '../../components/Common/FormModal';
import ConfirmModal from '../../components/Common/ConfirmModal';

const Users = () => {
    const [users, setUsers] = useState([]);
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);

    // Pagination state
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 20,
        total: 0,
        pages: 1
    });

    const [formData, setFormData] = useState({
        username: '',
        email: '',
        password: '',
        first_name: '',
        last_name: '',
        phone: '',
        role: 'customer'
    });

    useEffect(() => {
        fetchUsers();
    }, [pagination.page]);

    useEffect(() => {
        if (Array.isArray(users)) {
            const filtered = users.filter(user => {
                const searchLower = searchTerm.toLowerCase();
                return (
                    user.username?.toLowerCase().includes(searchLower) ||
                    user.email?.toLowerCase().includes(searchLower) ||
                    user.first_name?.toLowerCase().includes(searchLower) ||
                    user.last_name?.toLowerCase().includes(searchLower) ||
                    (user.phone && user.phone.toLowerCase().includes(searchLower))
                );
            });
            setFilteredUsers(filtered);
        }
    }, [searchTerm, users]);

    const fetchUsers = async () => {
        try {
            setLoading(true);
            setError('');
            const response = await usersAPI.list({ page: pagination.page, limit: pagination.limit });

            console.log('Users API Response:', response.data); // Debug log

            // Handle the specific API response structure
            let usersData = [];
            let paginationData = {};

            if (response.data && response.data.status === 'success' && response.data.data) {
                // Structure: { status: "success", data: { data: [...], meta: {...} } }
                if (response.data.data.data && Array.isArray(response.data.data.data)) {
                    usersData = response.data.data.data;
                }
                if (response.data.data.meta) {
                    paginationData = response.data.data.meta;
                }
            } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
                // Alternative structure: { data: [...] }
                usersData = response.data.data;
            } else if (Array.isArray(response.data)) {
                // Direct array response
                usersData = response.data;
            }

            if (!Array.isArray(usersData)) {
                console.error('Invalid users data format:', response.data);
                usersData = [];
            }

            setUsers(usersData);
            setFilteredUsers(usersData);
            setPagination(prev => ({
                ...prev,
                ...paginationData,
                total: paginationData.total || usersData.length,
                pages: paginationData.pages || Math.ceil((paginationData.total || usersData.length) / prev.limit)
            }));

        } catch (err) {
            console.error('Error fetching users:', err);
            setError(err.response?.data?.message || 'Failed to fetch users');
            setUsers([]);
            setFilteredUsers([]);
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = async (e) => {
        e.preventDefault();
        try {
            setError('');
            setSuccessMessage('');

            await usersAPI.create(formData);

            setSuccessMessage('User created successfully!');
            setShowCreateModal(false);
            resetForm();
            fetchUsers();

            setTimeout(() => setSuccessMessage(''), 3000);

        } catch (err) {
            console.error('Error creating user:', err);
            setError(err.response?.data?.message || 'Failed to create user');
        }
    };

    const handleUpdate = async (e) => {
        e.preventDefault();
        try {
            setError('');
            setSuccessMessage('');

            const updateData = { ...formData };
            if (!updateData.password) {
                delete updateData.password;
            }

            await usersAPI.update(selectedUser.id, updateData);

            setSuccessMessage('User updated successfully!');
            setShowEditModal(false);
            resetForm();
            setSelectedUser(null);
            setIsEditing(false);
            fetchUsers();

            setTimeout(() => setSuccessMessage(''), 3000);

        } catch (err) {
            console.error('Error updating user:', err);
            setError(err.response?.data?.message || 'Failed to update user');
        }
    };

    const handleDelete = async () => {
        if (!selectedUser?.id) {
            setError('User ID is missing');
            return;
        }

        try {
            setDeleteLoading(true);
            setError('');
            setSuccessMessage('');

            console.log('Attempting to delete user with ID:', selectedUser.id); // Debug log

            // Try different DELETE approaches
            const response = await usersAPI.delete(selectedUser.id);

            console.log('Delete API Response:', response.data); // Debug log

            setSuccessMessage('User deleted successfully!');
            setShowDeleteModal(false);
            setSelectedUser(null);
            fetchUsers();

            setTimeout(() => setSuccessMessage(''), 3000);

        } catch (err) {
            console.error('Error deleting user:', err);

            // Try to parse different error formats
            let errorMsg = 'Failed to delete user';

            if (err.response) {
                console.error('Delete Error Response:', err.response);

                if (err.response.data) {
                    if (err.response.data.message) {
                        errorMsg = err.response.data.message;
                    } else if (err.response.data.error) {
                        errorMsg = err.response.data.error;
                    } else if (typeof err.response.data === 'string') {
                        errorMsg = err.response.data;
                    }
                }

                // Handle specific status codes
                if (err.response.status === 403) {
                    errorMsg = 'Permission denied. You cannot delete this user.';
                } else if (err.response.status === 404) {
                    errorMsg = 'User not found. It may have already been deleted.';
                } else if (err.response.status === 400) {
                    errorMsg = 'Invalid request. Please check the user data.';
                }
            } else if (err.request) {
                errorMsg = 'Network error. Please check your connection.';
            }

            setError(errorMsg);
        } finally {
            setDeleteLoading(false);
        }
    };

    // Alternative delete method that might work with your API
    const handleDeleteAlternative = async () => {
        if (!selectedUser?.id) {
            setError('User ID is missing');
            return;
        }

        try {
            setDeleteLoading(true);
            setError('');
            setSuccessMessage('');

            console.log('Trying alternative delete for user ID:', selectedUser.id);

            // Some APIs require DELETE with a body or different format
            const response = await fetch(`http://localhost:8000/users/${selectedUser.id}`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });

            const data = await response.json();
            console.log('Alternative Delete Response:', data);

            if (!response.ok) {
                throw new Error(data.message || `HTTP error! status: ${response.status}`);
            }

            setSuccessMessage('User deleted successfully!');
            setShowDeleteModal(false);
            setSelectedUser(null);
            fetchUsers();

            setTimeout(() => setSuccessMessage(''), 3000);

        } catch (err) {
            console.error('Alternative delete error:', err);
            setError(err.message || 'Failed to delete user');
        } finally {
            setDeleteLoading(false);
        }
    };

    const handleStatusChange = async (userId, currentStatus) => {
        try {
            const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
            await usersAPI.updateStatus(userId, newStatus);
            fetchUsers();
        } catch (err) {
            console.error('Error changing user status:', err);
            setError('Failed to change user status');
        }
    };

    const openEditModal = (user) => {
        setSelectedUser(user);
        setFormData({
            username: user.username || '',
            email: user.email || '',
            password: '', // Don't pre-fill password for security
            first_name: user.first_name || '',
            last_name: user.last_name || '',
            phone: user.phone || '',
            role: user.role || 'customer'
        });
        setIsEditing(true);
        setShowEditModal(true);
        setError('');
    };

    const openDeleteModal = (user) => {
        setSelectedUser(user);
        setShowDeleteModal(true);
        setError('');
    };

    const resetForm = () => {
        setFormData({
            username: '',
            email: '',
            password: '',
            first_name: '',
            last_name: '',
            phone: '',
            role: 'customer'
        });
        setIsEditing(false);
        setSelectedUser(null);
        setError('');
    };

    const handleModalClose = () => {
        setShowCreateModal(false);
        setShowEditModal(false);
        resetForm();
    };

    const handlePageChange = (page) => {
        if (page >= 1 && page <= pagination.pages) {
            setPagination(prev => ({ ...prev, page }));
        }
    };

    const getStatusBadge = (status) => {
        switch (status?.toLowerCase()) {
            case 'active':
                return <Badge bg="success">Active</Badge>;
            case 'inactive':
                return <Badge bg="secondary">Inactive</Badge>;
            case 'pending':
                return <Badge bg="warning">Pending</Badge>;
            default:
                return <Badge bg="info">Active</Badge>;
        }
    };

    const getRoleBadge = (role) => {
        switch (role?.toLowerCase()) {
            case 'admin':
                return <Badge bg="danger">Admin</Badge>;
            case 'superadmin':
                return <Badge bg="dark">Super Admin</Badge>;
            case 'vendor':
                return <Badge bg="info">Vendor</Badge>;
            case 'customer':
                return <Badge bg="primary">Customer</Badge>;
            default:
                return <Badge bg="secondary">Customer</Badge>;
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        try {
            return new Date(dateString).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        } catch {
            return dateString;
        }
    };

    const renderPagination = () => {
        if (pagination.pages <= 1) return null;

        const items = [];
        const maxVisiblePages = 5;
        let startPage = Math.max(1, pagination.page - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(pagination.pages, startPage + maxVisiblePages - 1);

        if (endPage - startPage + 1 < maxVisiblePages) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }

        // Previous button
        items.push(
            <Pagination.Prev
                key="prev"
                disabled={pagination.page === 1}
                onClick={() => handlePageChange(pagination.page - 1)}
            />
        );

        // First page
        if (startPage > 1) {
            items.push(
                <Pagination.Item key={1} active={1 === pagination.page} onClick={() => handlePageChange(1)}>
                    1
                </Pagination.Item>
            );
            if (startPage > 2) {
                items.push(<Pagination.Ellipsis key="start-ellipsis" disabled />);
            }
        }

        // Page numbers
        for (let i = startPage; i <= endPage; i++) {
            items.push(
                <Pagination.Item
                    key={i}
                    active={i === pagination.page}
                    onClick={() => handlePageChange(i)}
                >
                    {i}
                </Pagination.Item>
            );
        }

        // Last page
        if (endPage < pagination.pages) {
            if (endPage < pagination.pages - 1) {
                items.push(<Pagination.Ellipsis key="end-ellipsis" disabled />);
            }
            items.push(
                <Pagination.Item
                    key={pagination.pages}
                    active={pagination.pages === pagination.page}
                    onClick={() => handlePageChange(pagination.pages)}
                >
                    {pagination.pages}
                </Pagination.Item>
            );
        }

        // Next button
        items.push(
            <Pagination.Next
                key="next"
                disabled={pagination.page === pagination.pages}
                onClick={() => handlePageChange(pagination.page + 1)}
            />
        );

        return items;
    };

    if (loading) return (
        <div className="text-center py-5">
            <div className="spinner-border text-success" role="status">
                <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2 text-muted">Loading users...</p>
        </div>
    );

    return (
        <div>
            <Row className="align-items-center mb-4">
                <Col>
                    <h1 className="mb-0">Users Management</h1>
                    <p className="text-muted mb-0">Manage system users and their permissions</p>
                </Col>
                <Col xs="auto">
                    <Button
                        variant="success"
                        onClick={() => {
                            resetForm();
                            setShowCreateModal(true);
                        }}
                        className="d-flex align-items-center"
                    >
                        <FaPlus className="me-2" />
                        Create User
                    </Button>
                </Col>
            </Row>

            {error && (
                <Alert variant="danger" dismissible onClose={() => setError('')}>
                    {error}
                </Alert>
            )}

            {successMessage && (
                <Alert variant="success" dismissible onClose={() => setSuccessMessage('')}>
                    {successMessage}
                </Alert>
            )}

            <Card className="border-0 shadow-sm mb-4">
                <Card.Body>
                    <Row className="g-3">
                        <Col md={6}>
                            <div className="position-relative">
                                <FaSearch className="position-absolute top-50 start-3 translate-middle-y text-muted" />
                                <Form.Control
                                    type="text"
                                    placeholder="Search users by name, email, or phone..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="ps-5"
                                />
                            </div>
                        </Col>
                        <Col md={6} className="text-md-end d-flex align-items-center justify-content-end">
                            <div className="me-4">
                                <small className="text-muted">
                                    Total: <strong>{pagination.total}</strong> •
                                    Showing: <strong>{searchTerm ? filteredUsers.length : users.length}</strong> users
                                </small>
                            </div>
                            {searchTerm && (
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={() => setSearchTerm('')}
                                >
                                    Clear Search
                                </Button>
                            )}
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            <Card className="border-0 shadow-sm mb-4">
                <Card.Body className="p-0">
                    <Table responsive hover className="mb-0">
                        <thead className="bg-light">
                            <tr>
                                <th>User</th>
                                <th>Contact</th>
                                <th>Role</th>
                                <th>Status</th>
                                <th>Created</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredUsers.length > 0 ? (
                                filteredUsers.map(user => (
                                    <tr key={user.id}>
                                        <td>
                                            <div className="d-flex align-items-center">
                                                <div
                                                    className="bg-success rounded-circle d-flex align-items-center justify-content-center me-3"
                                                    style={{ width: '40px', height: '40px' }}
                                                >
                                                    <FaUser className="text-white" />
                                                </div>
                                                <div>
                                                    <div className="fw-semibold">{user.username || 'N/A'}</div>
                                                    <small className="text-muted">
                                                        {user.first_name || ''} {user.last_name || ''}
                                                        {!user.first_name && !user.last_name && 'No name provided'}
                                                    </small>
                                                    <div className="text-muted small">
                                                        ID: {user.id?.substring(0, 12)}...
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="small">
                                                <div className="fw-medium">{user.email || '-'}</div>
                                                {user.phone && (
                                                    <div className="text-muted">{user.phone}</div>
                                                )}
                                            </div>
                                        </td>
                                        <td>
                                            {getRoleBadge(user.role)}
                                        </td>
                                        <td>
                                            <div className="d-flex align-items-center">
                                                {getStatusBadge(user.status)}
                                                {user.status && (
                                                    <Button
                                                        variant="link"
                                                        size="sm"
                                                        className="ms-2 p-0"
                                                        onClick={() => handleStatusChange(user.id, user.status)}
                                                        title={`Change to ${user.status === 'active' ? 'inactive' : 'active'}`}
                                                    >
                                                        {user.status === 'active' ? (
                                                            <FaUserTimes className="text-danger" />
                                                        ) : (
                                                            <FaUserCheck className="text-success" />
                                                        )}
                                                    </Button>
                                                )}
                                            </div>
                                        </td>
                                        <td>
                                            <div className="small">
                                                <div>{formatDate(user.created_at)}</div>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="d-flex gap-2">
                                                <Button
                                                    variant="outline-primary"
                                                    size="sm"
                                                    className="d-flex align-items-center"
                                                    onClick={() => openEditModal(user)}
                                                    title="Edit User"
                                                >
                                                    <FaEdit className="me-1" />
                                                    Edit
                                                </Button>
                                                <Button
                                                    variant="outline-danger"
                                                    size="sm"
                                                    className="d-flex align-items-center"
                                                    onClick={() => openDeleteModal(user)}
                                                    title="Delete User"
                                                    disabled={user.role === 'admin' || user.role === 'superadmin'}
                                                >
                                                    <FaTrash className="me-1" />
                                                    Delete
                                                </Button>
                                            </div>
                                            {(user.role === 'admin' || user.role === 'superadmin') && (
                                                <small className="text-muted d-block mt-1">
                                                    Cannot delete admin users
                                                </small>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="6" className="text-center py-5">
                                        <div className="text-muted">
                                            {searchTerm ? (
                                                <>
                                                    <FaSearch className="h3 mb-3" />
                                                    <p>No users found matching "{searchTerm}"</p>
                                                </>
                                            ) : (
                                                <>
                                                    <FaUser className="h3 mb-3" />
                                                    <p>No users found</p>
                                                </>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </Card.Body>
            </Card>

            {/* Pagination */}
            {pagination.pages > 1 && (
                <Card className="border-0 shadow-sm">
                    <Card.Body className="py-3">
                        <div className="d-flex justify-content-between align-items-center">
                            <div>
                                <small className="text-muted">
                                    Page {pagination.page} of {pagination.pages} •
                                    Showing {users.length} of {pagination.total} users
                                </small>
                            </div>
                            <Pagination className="mb-0">
                                {renderPagination()}
                            </Pagination>
                        </div>
                    </Card.Body>
                </Card>
            )}

            {/* Stats Summary */}
            {users.length > 0 && (
                <Row className="mt-4 g-3">
                    <Col md={3}>
                        <Card className="border-0 shadow-sm bg-primary text-white">
                            <Card.Body className="text-center py-3">
                                <div className="h4 mb-1">{pagination.total}</div>
                                <small>Total Users</small>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={3}>
                        <Card className="border-0 shadow-sm bg-success text-white">
                            <Card.Body className="text-center py-3">
                                <div className="h4 mb-1">
                                    {users.filter(u => u.status === 'active' || !u.status).length}
                                </div>
                                <small>Active Users</small>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={3}>
                        <Card className="border-0 shadow-sm bg-info text-white">
                            <Card.Body className="text-center py-3">
                                <div className="h4 mb-1">
                                    {users.filter(u => u.role === 'admin' || u.role === 'superadmin').length}
                                </div>
                                <small>Admin Users</small>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={3}>
                        <Card className="border-0 shadow-sm bg-warning text-dark">
                            <Card.Body className="text-center py-3">
                                <div className="h4 mb-1">
                                    {users.filter(u => u.role === 'customer' || !u.role).length}
                                </div>
                                <small>Customers</small>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            )}

            {/* Create Modal */}
            <FormModal
                show={showCreateModal}
                onHide={handleModalClose}
                title="Create New User"
                onSubmit={handleCreate}
                submitText="Create User"
                size="lg"
            >
                <Row>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Username *</Form.Label>
                            <Form.Control
                                type="text"
                                value={formData.username}
                                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                                required
                                placeholder="Enter username"
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Email *</Form.Label>
                            <Form.Control
                                type="email"
                                value={formData.email}
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                required
                                placeholder="Enter email"
                            />
                        </Form.Group>
                    </Col>
                </Row>

                <Form.Group className="mb-3">
                    <Form.Label>Password *</Form.Label>
                    <Form.Control
                        type="password"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        required
                        placeholder="Enter password"
                    />
                    <Form.Text className="text-muted">
                        Minimum 6 characters
                    </Form.Text>
                </Form.Group>

                <Row>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>First Name</Form.Label>
                            <Form.Control
                                type="text"
                                value={formData.first_name}
                                onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                                placeholder="Enter first name"
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Last Name</Form.Label>
                            <Form.Control
                                type="text"
                                value={formData.last_name}
                                onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                                placeholder="Enter last name"
                            />
                        </Form.Group>
                    </Col>
                </Row>

                <Row>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Phone</Form.Label>
                            <Form.Control
                                type="tel"
                                value={formData.phone}
                                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                placeholder="Enter phone number"
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Role</Form.Label>
                            <Form.Select
                                value={formData.role}
                                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                            >
                                <option value="customer">Customer</option>
                                <option value="vendor">Vendor</option>
                                <option value="admin">Admin</option>
                            </Form.Select>
                        </Form.Group>
                    </Col>
                </Row>
            </FormModal>

            {/* Edit Modal */}
            <FormModal
                show={showEditModal}
                onHide={handleModalClose}
                title={`Edit User: ${selectedUser?.username || ''}`}
                onSubmit={handleUpdate}
                submitText="Update User"
                size="lg"
            >
                <Row>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Username *</Form.Label>
                            <Form.Control
                                type="text"
                                value={formData.username}
                                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                                required
                                placeholder="Enter username"
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Email *</Form.Label>
                            <Form.Control
                                type="email"
                                value={formData.email}
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                required
                                placeholder="Enter email"
                            />
                        </Form.Group>
                    </Col>
                </Row>

                <Form.Group className="mb-3">
                    <Form.Label>Password</Form.Label>
                    <Form.Control
                        type="password"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        placeholder="Enter new password (leave blank to keep current)"
                    />
                    <Form.Text className="text-muted">
                        Leave password field blank if you don't want to change it
                    </Form.Text>
                </Form.Group>

                <Row>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>First Name</Form.Label>
                            <Form.Control
                                type="text"
                                value={formData.first_name}
                                onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                                placeholder="Enter first name"
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Last Name</Form.Label>
                            <Form.Control
                                type="text"
                                value={formData.last_name}
                                onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                                placeholder="Enter last name"
                            />
                        </Form.Group>
                    </Col>
                </Row>

                <Row>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Phone</Form.Label>
                            <Form.Control
                                type="tel"
                                value={formData.phone}
                                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                placeholder="Enter phone number"
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Role</Form.Label>
                            <Form.Select
                                value={formData.role}
                                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                            >
                                <option value="customer">Customer</option>
                                <option value="vendor">Vendor</option>
                                <option value="admin">Admin</option>
                            </Form.Select>
                        </Form.Group>
                    </Col>
                </Row>
            </FormModal>

            {/* Delete Confirmation Modal */}
            <ConfirmModal
                show={showDeleteModal}
                onHide={() => setShowDeleteModal(false)}
                onConfirm={handleDelete}
                title="Delete User"
                message={
                    <div>
                        <p>Are you sure you want to delete user <strong>"{selectedUser?.username}"</strong>?</p>
                        <p className="text-danger mb-0">This action cannot be undone.</p>
                        {selectedUser?.role === 'admin' || selectedUser?.role === 'superadmin' ? (
                            <Alert variant="warning" className="mt-3 mb-0">
                                <FaUser className="me-2" />
                                <strong>Warning:</strong> Admin users cannot be deleted.
                                Please demote them to customer role first.
                            </Alert>
                        ) : null}
                    </div>
                }
                confirmText={deleteLoading ? "Deleting..." : "Delete"}
                confirmDisabled={deleteLoading || selectedUser?.role === 'admin' || selectedUser?.role === 'superadmin'}
                variant="danger"
                showAlternativeButton={true}
                alternativeText="Try Alternative Delete"
                onAlternative={() => handleDeleteAlternative()}
            />
        </div>
    );
};

export default Users;